import { ChangeEvent, Dispatch, useEffect, SyntheticEvent } from 'react';
import { RadioGroup, RadioButton } from '@facephi/ui-react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useConfig } from '../../providers';
import {
  VoiceValidationType,
  customPhraseSchema,
  userLastNameState,
  userNameState,
  voiceValidationCustomState,
  voiceValidationTypeState,
} from '../../states';
import { CustomSentenceInput } from './Styles';

type VoiceTypesSelectionProps = {
  setDisableButton?: Dispatch<boolean>;
  disabled?: boolean;
};

export const VoiceTypesSelection = ({
  setDisableButton,
  disabled,
}: VoiceTypesSelectionProps) => {
  const { t } = useTranslation();
  const { setCustomPhraseValid } = useConfig();
  const userName = useRecoilValue(userNameState);
  const userLastName = useRecoilValue(userLastNameState);

  const [voiceVerifType, setVoiceVerifType] = useRecoilState(
    voiceValidationTypeState
  );
  const [voiceVerifCustom, setVoiceVerifCustom] = useRecoilState(
    voiceValidationCustomState
  );

  const changeSelectionHandler = (e: ChangeEvent<HTMLInputElement>) => {
    const selection = e.target.value as VoiceValidationType;
    if (selection !== VoiceValidationType.custom) {
      setVoiceVerifCustom('');
    }
    setVoiceVerifType(
      VoiceValidationType[e.target.value as VoiceValidationType]
    );
  };

  const handleInputChange = (
    event: SyntheticEvent<HTMLInputElement> | undefined
  ) => {
    if (event) setVoiceVerifCustom(event?.currentTarget?.value);
  };

  const {
    register,
    reset,
    getValues,
    formState: { errors },
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(customPhraseSchema),
  });

  const disableInput = voiceVerifType !== VoiceValidationType.custom;
  const hasError = !!Object.keys(errors).length;

  const voiceValidationList: {
    id: VoiceValidationType;
    title: string;
    active: boolean;
    testId: string;
  }[] = [
    {
      id: VoiceValidationType.nameLastname,
      title: `"${userName} ${userLastName}"`,
      active: !!userName || !!userLastName,
      testId: 'voice_phrase_selector_option_full_name',
    },
    {
      id: VoiceValidationType.documentNumber,
      title: t('Your id card number'),
      active: true,
      testId: 'voice_phrase_selector_option_document_number',
    },
    {
      id: VoiceValidationType.facephiBank,
      title: `"${t('My bank is Facephi')}"`,
      active: true,
      testId: 'voice_phrase_selector_option_my_bank',
    },
    {
      id: VoiceValidationType.custom,
      title: t('Enter a custom phrase'),
      active: true,
      testId: 'voice_phrase_selector_option_custom_phrase',
    },
  ];

  useEffect(() => {
    const formHasValues = getValues().customPhrase;
    setCustomPhraseValid(
      voiceVerifType !== VoiceValidationType.custom ||
        (!hasError && !!formHasValues)
    );
    setDisableButton &&
      setDisableButton(
        voiceVerifType === VoiceValidationType.custom &&
          (hasError || !formHasValues)
      );
  }, [voiceVerifType, hasError, getValues]);

  useEffect(() => {
    if (voiceVerifType === VoiceValidationType.custom && voiceVerifCustom) {
      reset({ customPhrase: voiceVerifCustom });
    } else {
      reset({ customPhrase: '' });
    }
  }, [voiceVerifType]);

  return (
    <>
      <RadioGroup
        name="voice-message"
        defaultSelected={voiceVerifType}
        onChange={changeSelectionHandler}
        testId="voice-message"
        rowGap="2"
      >
        {voiceValidationList
          .filter(({ active }) => active)
          .map(({ id, title, testId }) => (
            <RadioButton
              key={id}
              label={t(title)}
              value={id}
              disabled={disabled}
              testId={testId}
            />
          ))}
      </RadioGroup>
      <CustomSentenceInput
        {...register('customPhrase')}
        placeholder={String(t('Custom phrase'))}
        disabled={disableInput || disabled}
        errorLabel={
          errors?.customPhrase?.message && t(errors?.customPhrase?.message)
        }
        $error={hasError}
        onKeyUp={handleInputChange}
        testId="voice_phrase_selector_custom_text_box"
      />
    </>
  );
};
